import React from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import OnlineEventsBannerContainer from '../containers/OnlineEventsBannerContainer';

import {
    HAS_DOCUMENT,
    HAS_WINDOW,
    addWindowListener,
    removeWindowListener,
} from '@eb/eds-utils';
import { Layout } from '@eb/eds-layout';
import { HAS_WINDOW_LISTENERS, getWindowObject } from '@eb/feature-detection';

import {
    withEventSaveNotification,
    constants as destinationConstants,
} from '@eb/destination-utils';

import CityBrowseHeader from './CityBrowseHeader';
import MainContent from './MainContent';
import CityBrowseFooter from './CityBrowseFooter';

import {
    ARTIST_SOCIAL_LINKS_PROPTYPE,
    GA_CITY_BROWSE_CATEGORY,
    GA_SCROLL_DEPTH_ACTION,
    GA_SCROLL_COMPLETE_LABEL,
    GA_SCROLL_NA_LABEL,
    LOCATION_SUGGESTION_PROPTYPE,
} from '../constants';

const { LOCATION_DIALOG_CONTENT } = destinationConstants;

const A2H_VISIT = 'addToHomescreenVisit';
const SCROLL_DEBOUNCE_TIME = 200;
const ONLINE_EVENT_BANNER_STYLES = {
    maxWidth: '1276px',
    margin: '0 auto',
    padding: '32px 0',
};

const trackScrollPosition = (trackGAEvent) => {
    if (document.body.scrollHeight <= window.innerHeight + window.scrollY) {
        trackGAEvent({
            category: GA_CITY_BROWSE_CATEGORY,
            label: GA_SCROLL_COMPLETE_LABEL,
            action: GA_SCROLL_DEPTH_ACTION,
        });
    }
};

const trackAddToHomescreenLink = (trackGAEvent) => {
    const matchMedia = getWindowObject('matchMedia');
    const navigator = getWindowObject('navigator');

    // Track when users arrive to city browse through an add to homescreen link
    if (matchMedia && matchMedia('(display-mode: standalone)').matches) {
        trackGAEvent({
            action: A2H_VISIT,
        });
    } else {
        // Tracking in Safari
        // eslint-disable-next-line no-lonely-if
        if (navigator && navigator.standalone === true) {
            trackGAEvent({
                action: A2H_VISIT,
            });
        }
    }
};

const BannerStyleProvider = ({ children }) => {
    // If the banner chooses not to render, don't
    // show the styles
    if (!children) {
        return null;
    }

    return <div style={ONLINE_EVENT_BANNER_STYLES}>{children}</div>;
};

class CityBrowseBaseScreen extends React.PureComponent {
    static propTypes = {
        domain: PropTypes.string.isRequired,
        /*
         * From withOverlayControls HoC
         */
        showOverlay: PropTypes.func.isRequired,
        /*
         * An array of "Bucket" objects which correlate to each major section of information
         * on the apge
         */
        bucketConfigs: PropTypes.arrayOf(PropTypes.object).isRequired,
        /*
         * Tracker
         */
        onTrackPageViewAction: PropTypes.func.isRequired,
        /*
         * Array of location suggestions for consumption by
         * the location switcher
         */
        locationSuggestions: LOCATION_SUGGESTION_PROPTYPE,
        /*
         * Callback to display updated autocomplete suggestions
         */
        handleLocationSuggestionChange: PropTypes.func,
        /*
         * Callback for handling changing of the selected metro
         */
        handleLocationSelect: PropTypes.func,
        /*
         * Brief city description for the selected metro
         */
        cityBlurb: PropTypes.string,
        /*
         * Image to be displayed in the metro's header
         */
        headerImageUrl: PropTypes.string,
        /*
         * Image to be displayed in the metro's header on small viewports
         */
        headerSmallImageUrl: PropTypes.string,
        /*
         * Image to be displayed in the middle section of the page for selected metros
         */
        middleImageUrl: PropTypes.string,
        /*
         * Small Image to be displayed in the middle section of the page on small viewports
         */
        middleSmallImageUrl: PropTypes.string,
        /*
         * Image to be displayed in the footer section of the page for selected metros
         */
        footerImageUrl: PropTypes.string,
        /*
         * Image to be displayed in the footer section of the page on small viewports
         */
        footerSmallImageUrl: PropTypes.string,
        /*
         * The artist's chosen image to represent themselves
         */
        artistImageUrl: PropTypes.string,
        /*
         * Decorative image provided by artist
         */
        decorationTopLeftUrl: PropTypes.string,
        /*
         * Decorative image provided by artist
         */
        decorationTopRightUrl: PropTypes.string,
        /*
         * Decorative image provided by artist
         */
        decorationBottomLeftUrl: PropTypes.string,
        /*
         * Decorative image provided by artist
         */
        decorationBottomRightUrl: PropTypes.string,
        /*
         * Artist summary provided for the artist's attribution
         */
        artistSummary: PropTypes.string,
        /*
         * Artist name
         */
        artistName: PropTypes.string,
        /*
         * Artist social links
         */
        artistSocialLinks: ARTIST_SOCIAL_LINKS_PROPTYPE,
        /*
         * Tracking event from state from @eb/site-analytics
         */
        trackGAEvent: PropTypes.func,
        /*
         * Should hide mask
         */
        shouldHideMask: PropTypes.bool,

        featureFlags: PropTypes.shape({
            useCurrentLocation: PropTypes.bool,
        }),

        handleUseCurrentLocation: PropTypes.func,

        locationDenied: PropTypes.bool,

        currentPlace: PropTypes.string,

        h1Prefix: PropTypes.string,

        isWaitingForLocation: PropTypes.bool,

        isOnline: PropTypes.bool,
        onSaveEvent: PropTypes.func,
        isAuthenticated: PropTypes.bool,
    };

    componentDidMount() {
        const { onTrackPageViewAction, trackGAEvent } = this.props;

        this._debouncedTrackScrollPosition = debounce(
            trackScrollPosition.bind(null, trackGAEvent),
            SCROLL_DEBOUNCE_TIME,
        );
        this._handleScrollTracking();

        // track pageview GA action
        onTrackPageViewAction(true);
        trackAddToHomescreenLink(trackGAEvent);
    }

    componentDidUpdate(prevProps) {
        const { locationDenied, showOverlay } = this.props;

        if (locationDenied !== prevProps.locationDenied) {
            showOverlay('dialog', LOCATION_DIALOG_CONTENT);
        }
    }

    componentWillUnmount() {
        removeWindowListener('scroll', this._debouncedTrackScrollPosition);
    }

    _onUseCurrentLocation = () => {
        const {
            handleUseCurrentLocation,
            showOverlay,
            locationDenied,
        } = this.props;

        if (locationDenied) {
            return showOverlay('dialog', LOCATION_DIALOG_CONTENT);
        }

        return handleUseCurrentLocation(true);
    };

    _handleScrollTracking() {
        if (HAS_WINDOW && HAS_WINDOW_LISTENERS && HAS_DOCUMENT) {
            if (document.body.scrollHeight === window.innerHeight) {
                const { trackGAEvent } = this.props;

                trackGAEvent({
                    category: GA_CITY_BROWSE_CATEGORY,
                    label: GA_SCROLL_NA_LABEL,
                    action: GA_SCROLL_DEPTH_ACTION,
                });
            }
        }

        addWindowListener('scroll', this._debouncedTrackScrollPosition);
    }

    render() {
        let {
            artistImageUrl,
            artistName,
            artistSocialLinks,
            artistSummary,
            bucketConfigs,
            cityBlurb,
            currentPlace,
            decorationBottomLeftUrl,
            decorationBottomRightUrl,
            decorationTopLeftUrl,
            decorationTopRightUrl,
            domain,
            experiments,
            featureFlags,
            footerImageUrl,
            footerSmallImageUrl,
            h1Prefix,
            handleLocationSelect,
            handleLocationSuggestionChange,
            headerImageUrl,
            headerSmallImageUrl,
            isOnline,
            isWaitingForLocation,
            locationSuggestions,
            middleImageUrl,
            middleSmallImageUrl,
            shouldHideMask,
            trackGAEvent,
        } = this.props;

        return (
            <Layout>
                <BannerStyleProvider>
                    <OnlineEventsBannerContainer shouldDirectLink={true} />
                </BannerStyleProvider>
                <CityBrowseHeader
                    locationSuggestions={locationSuggestions}
                    handleLocationSuggestionChange={
                        handleLocationSuggestionChange
                    }
                    handleLocationSelect={handleLocationSelect}
                    currentPlace={currentPlace}
                    cityBlurb={cityBlurb}
                    h1Prefix={h1Prefix}
                    cityImage={headerImageUrl}
                    citySmallImage={headerSmallImageUrl}
                    shouldHideMask={shouldHideMask}
                    experiments={experiments}
                    domain={domain}
                    trackGAEvent={trackGAEvent}
                    isUseCurrentLocationEnabled={
                        featureFlags.useCurrentLocation
                    }
                    handleUseCurrentLocation={this._onUseCurrentLocation}
                    isOnline={isOnline}
                    isWaitingForLocation={isWaitingForLocation}
                    history={this.props.history}
                />
                <MainContent
                    bucketConfigs={bucketConfigs}
                    middleImage={middleImageUrl}
                    middleSmallImage={middleSmallImageUrl}
                    decorationTopLeftUrl={decorationTopLeftUrl}
                    decorationTopRightUrl={decorationTopRightUrl}
                    decorationBottomLeftUrl={decorationBottomLeftUrl}
                    decorationBottomRightUrl={decorationBottomRightUrl}
                    featureFlags={featureFlags}
                />
                <CityBrowseFooter
                    footerImage={footerImageUrl}
                    footerSmallImage={footerSmallImageUrl}
                    artistImage={artistImageUrl}
                    artistSummary={artistSummary}
                    artistName={artistName}
                    artistSocialLinks={artistSocialLinks}
                    experiments={experiments}
                />
            </Layout>
        );
    }
}

export default withEventSaveNotification(CityBrowseBaseScreen);
